import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import queryString from 'query-string';
import SchedularHeader from '../SchedularHeader/SchedularHeader';
import AvailabilityCalendar from '../../Containers/AvailabilityCalendar/AvailabilityCalendar';
import ResponseMessage from '../SlotSchedulerResponseMessage/SlotSchedulerResponseMessage';
import ConnectRepository from '../../Repositories/ConnectRepository';
import { getRescheduleToken, toRescheduleView, describeRescheduleFailure } from '../../Utility/rescheduleLink';
import { getInviteToken } from '../../Utility/inviteLink';
import { toInstant } from '../../Utility/proposeLink';
import './CandidateSlotSchedular.scss';

const decodeFromBase64Url = (content) => {
  if (!content) return null;
  try {
    let base64 = content.replace(/-/g, '+').replace(/_/g, '/');

    const padding = 4 - (base64.length % 4);
    if (padding < 4) {
      base64 += '='.repeat(padding);
    }
    const jsonString = Buffer.from(base64, 'base64');
    return JSON.parse(jsonString);
  } catch {
    return {};
  }
};

const connectRepository = new ConnectRepository();
const today = moment();

const CandidateSlotSchedular = (props) => {
  const {
    settings,
    eventsById,
    fetchSettings,
    getEventById,
    conversation,
    fetchConversation,
    getEventApiStatus,
    getConversationApiStatus,
  } = props;

  const urlParams = queryString.parse(window.location.search);
  const { eventId, ref, content, nofs } = urlParams;

  // A `rescheduleToken` link carries an opaque token instead of the recruiter's
  // address; nothing on the query string is trusted as an identity.
  const rescheduleToken = getRescheduleToken();
  const inviteToken = getInviteToken();
  const [rescheduleState, setRescheduleState] = useState({ status: 'INPROGRESS', view: null, failure: null });
  const [resolveAttempt, setResolveAttempt] = useState(0);

  const [showCalendar, setShowCalendar] = useState(false);
  const [isDataReady, setIsDataReady] = useState(false);
  const [dataSource, setDataSource] = useState(null);
  const [calendarMessage, setCalendarMessage] = useState({
    title: 'You have scheduled a meeting',
    description: 'A calendar invitation has been sent to your email address',
  });

  useEffect(() => {
    if (!rescheduleToken || !eventId) return undefined;
    let isStale = false;
    setRescheduleState({ status: 'INPROGRESS', view: null, failure: null });
    connectRepository
      .resolveRescheduleContext(eventId, rescheduleToken)
      .then(response => {
        if (!isStale) setRescheduleState({ status: 'COMPLETED', view: toRescheduleView(response.data), failure: null });
      })
      .catch(error => {
        // Only the status is read: the failed request carries the token.
        if (!isStale) setRescheduleState({ status: 'FAILED', view: null, failure: describeRescheduleFailure(error) });
      });
    return () => {
      isStale = true;
    };
  }, [rescheduleToken, eventId, resolveAttempt]);

  useEffect(() => {
    if (!eventId) {
      setIsDataReady(true);
      return;
    }
    // The calendar api is keyed by the recruiter's email header: a reschedule link never has
    // it, and an invite link has it only once the invite endpoint has verified its token.
    if (rescheduleToken) return;
    if (inviteToken) {
      fetchConversation(eventId, inviteToken);
      return;
    }

    fetchSettings();
    getEventById(eventId);
  }, [rescheduleToken, inviteToken, eventId, fetchSettings, getEventById, fetchConversation]);

  useEffect(() => {
    if (inviteToken && conversation && conversation.ReferenceId === eventId) fetchSettings();
  }, [inviteToken, conversation, eventId, fetchSettings]);

  useEffect(() => {
    if (rescheduleToken || inviteToken || !eventId) return;

    const eventExists =
      eventsById && eventsById[eventId] && eventsById[eventId].data && eventsById[eventId].data.EventId;

    if (eventExists) {
      setShowCalendar(false);
      setDataSource('event');
      setIsDataReady(true);
    } else if (getEventApiStatus === 'COMPLETED' && ref && ref.toLowerCase() === 'conversation') {
      fetchConversation(eventId);
    } else if (getEventApiStatus === 'COMPLETED' && eventsById) {
      setDataSource(null);
      setIsDataReady(true);
    }
  }, [eventsById, eventId, ref, getEventApiStatus]);

  useEffect(() => {
    if (rescheduleToken) return;
    if (conversation && conversation.ReferenceId === eventId) {
      const decodedEvent = decodeFromBase64Url(content);
      const expiryEnd = decodedEvent && toInstant(decodedEvent.ExpiryEnd);
      if (decodedEvent && ((expiryEnd && today.isAfter(moment(expiryEnd))) || conversation.JobStatus.toLowerCase() === 'closed')) {
        setShowCalendar(false);
        setCalendarMessage({
          title: 'This link is no longer valid',
          description: 'The job you are trying to schedule an interview for has been closed',
        });
      } else {
        setShowCalendar(true);
      }
      setDataSource('conversation');
      setIsDataReady(true);
    }
    if (getConversationApiStatus === 'REFUSED') {
      setShowCalendar(false);
      setCalendarMessage({
        title: 'This link is no longer valid',
        description: 'Please ask your recruiter for a new invitation link.',
      });
      setIsDataReady(true);
    }
    if (getConversationApiStatus === 'FAILED') {
      setShowCalendar(false);
      setIsDataReady(true);
    }
  }, [conversation, eventId, getConversationApiStatus]);

  const renderLoader = () => (
    <div className="loader-container">
      <div className="loader" />
    </div>
  );

  const renderRescheduleFlow = () => {
    if (rescheduleState.status === 'INPROGRESS') return renderLoader();

    if (rescheduleState.status === 'FAILED') {
      const { title, description, canRetry } = rescheduleState.failure;
      return (
        <>
          <SchedularHeader showCalendar={false} />
          <div className="slot-schedular-body centered">
            <ResponseMessage title={title} description={description}>
              {canRetry ? (
                <Button type="primary" onClick={() => setResolveAttempt(attempt => attempt + 1)}>
                  Try again
                </Button>
              ) : null}
            </ResponseMessage>
          </div>
        </>
      );
    }

    // Availability (GET freeslots) and the commit (PUT events/{id}) live in the
    // arya-calendar service, still keyed by the recruiter email header a token
    // link does not have. A picker drawn here would dead-end on click, so the
    // candidate gets the facts and a route to a human instead.
    // Copy Link stays off: it would copy the bearer token to the clipboard.
    const { recruiterName, holdStartTimeUtc } = rescheduleState.view;
    const contact = recruiterName || 'your recruiter';
    const heldAt = holdStartTimeUtc
      ? moment
          .utc(holdStartTimeUtc)
          .local()
          .format('MMMM D, YYYY [at] h:mm A')
      : null;

    return (
      <>
        <SchedularHeader showCalendar={false} />
        <div className="slot-schedular-body centered">
          <ResponseMessage
            title="Rescheduling from this link is not available yet"
            description={`Your interview with ${contact} is still held${
              heldAt ? ` for ${heldAt}` : ''
            }. To move it, reply to your interview invitation email and ${contact} will arrange a new time.`}
          />
        </div>
      </>
    );
  };

  if (rescheduleToken && eventId) return renderRescheduleFlow();

  if (!isDataReady) {
    return renderLoader();
  }

  const getEventData = () => {
    if (dataSource === 'event') {
      return {
        event: eventsById[eventId].data || eventsById[eventId],
        expiryStartTime: null,
        expiryEndTime: null,
      };
    }

    if (dataSource === 'conversation') {
      const decodedEvent = decodeFromBase64Url(content);
      return {
        event: { ...decodedEvent, ...conversation },
        expiryStartTime: decodedEvent ? decodedEvent.ExpiryStart : null,
        expiryEndTime: decodedEvent ? decodedEvent.ExpiryEnd : null,
      };
    }

    return {
      event: {},
      expiryStartTime: null,
      expiryEndTime: null,
    };
  };

  const { event, expiryStartTime, expiryEndTime } = getEventData();
  const expiryStart = toInstant(expiryStartTime);
  const expiryEnd = toInstant(expiryEndTime);

  if (!eventId) {
    return (
      <>
        <SchedularHeader showCalendar={false} />
        <div className="slot-schedular-body centered">
          <ResponseMessage title="Invalid Invite Link" description="Please make sure the link is valid" />
        </div>
      </>
    );
  }

  return (
    <>
      <SchedularHeader showCalendar={showCalendar} />
      <div className={`slot-schedular-body ${!showCalendar ? 'centered' : ''}`}>
        {showCalendar ? (
          <>
            <div className="info">
              <div className="slot-duration">{_.get(settings, 'SlotDuration', 30)} Minutes</div>
              <div className="name"> Hi, {event.CandidateName} </div>
              <div className="calendar-schedule-text">Confirm {event.JobTitle} Interview</div>
              <div className="calendar-schedule-description">
                We can&#39;t wait to meet you! Please choose a time that works best for your interview for the{' '}
                <span className="text-bold">{event.JobTitle}</span> role
                {expiryStart && expiryEnd ? (
                  <>
                    {' '}between <span className="text-bold">{moment(expiryStart).format('MMMM D, YYYY')}</span> to{' '}
                    <span className="text-bold">{moment(expiryEnd).format('MMMM D, YYYY')}</span>
                  </>
                ) : '.'}
              </div>
            </div>
            <div className="separator" />
            <AvailabilityCalendar
              settings={settings}
              event={event}
              setShowCalendar={setShowCalendar}
              expiryStartTime={expiryStartTime}
              expiryEndTime={expiryEndTime}
              dataSource={dataSource}
              disableFetchSlots={nofs === "true"}
            />
          </>
        ) : (
          <ResponseMessage title={calendarMessage.title} description={calendarMessage.description} />
        )}
      </div>
    </>
  );
};

export default CandidateSlotSchedular;
